import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { PageHeader, Card, Table, Badge, InfoAlert, Pagination, Input, Btn, FilterSelect } from '../components/UI';
import { fmt } from '../utils/helpers';

/**
 * Connection log search — "who was on this IP at this time?".
 *
 * Every lodge shares one flat 192.168.1.0/24 and one rotating Starlink CGNAT
 * public address, so an IP alone identifies nobody. An IP *plus a timestamp*
 * identifies a device, and the device maps to an account.
 */
export default function Sessions() {
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);
  const [lodges, setLodges]   = useState([]);
  const [retention, setRetention] = useState(null);
  const [q, setQ] = useState({ ip: '', mac: '', from: '', to: '', lodge_id: '' });
  const toast = useToast();

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 25 };
      Object.entries(q).forEach(([k, v]) => { if (v) params[k] = v; });
      const d = await api.getSessions(params);
      setRows(d.sessions || []);
      setTotal(d.total || 0);
      setRetention(d.retention_days ?? null);
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [page, q]); // eslint-disable-line

  useEffect(() => { load(1); setPage(1); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    api.getLodgesAdmin().then(d => setLodges(d.lodges || [])).catch(() => {});
  }, []);

  function search(e) {
    e?.preventDefault();
    setPage(1);
    load(1);
  }

  function reset() {
    setQ({ ip: '', mac: '', from: '', to: '', lodge_id: '' });
    setPage(1);
    setTimeout(() => load(1), 0);
  }

  const columns = [
    {
      key: 'when', label: 'Seen',
      render: (s) => (
        <div>
          <div style={{ color: 'var(--text)', fontSize: 12 }}>{fmt.datetime(s.last_seen)}</div>
          <div style={{ color: 'var(--text3)', fontSize: 11 }}>from {fmt.datetime(s.first_seen)}</div>
        </div>
      ),
    },
    {
      key: 'ip', label: 'Addresses',
      render: (s) => {
        const v6 = s.ipv6 || '';
        const global6 = /^[23][0-9a-f]{3}:/i.test(v6);
        return (
          <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 11.5 }}>
            <div style={{ color: 'var(--text)' }}>{s.ip_address || '—'}</div>
            {v6 && (
              <div style={{ color: global6 ? 'var(--green)' : 'var(--text3)', wordBreak: 'break-all' }}
                   title={global6
                     ? 'Globally routable — this is what an outside report would quote'
                     : 'Private address, does not leave the network'}>
                {v6}{global6 ? ' ●' : ''}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'mac', label: 'Device MAC',
      render: (s) => (
        <div>
          <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 12, color: 'var(--text)' }}>{s.mac_address}</div>
          {s.hostname && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.hostname}</div>}
        </div>
      ),
    },
    {
      key: 'user', label: 'Account',
      render: (s) => s.user
        ? <div>
            <div style={{ fontSize: 12, color: 'var(--text)' }}>{s.user.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.user.email}</div>
          </div>
        : <Badge color="gray">unattributed</Badge>,
    },
    {
      key: 'where', label: 'Access point',
      render: (s) => (
        <div>
          <div style={{ fontSize: 12, color: 'var(--text2)' }}>{s.ap_name || '— wired —'}</div>
          {s.lodge && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.lodge.name}</div>}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Connection Logs"
        subtitle="Search by IP, device or time to find who was using the network."
      />

      <InfoAlert>
        The IPv4 shown is a LAN address and is only meaningful together with a time — DHCP reassigns it.
        The public IPv4 is shared by everyone on a site (Starlink CGNAT) and identifies nobody.
        A green IPv6 marked ● is <strong>globally routable</strong> — that one is unique to the device
        and is what an external abuse report will quote, so search on it when you have one.
        Records are kept for {retention ? `${retention} days` : 'the configured retention period'}, and show the{' '}
        <strong>device</strong>, not necessarily the person using it.
      </InfoAlert>

      <Card style={{ marginTop: 16 }}>
        <form onSubmit={search} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, alignItems: 'flex-end' }}>
          <Input label="IP address"  value={q.ip}  onChange={v => setQ({ ...q, ip: v })}  placeholder="192.168.1.196 or 2605:59c0:…" />
          <Input label="Device MAC"  value={q.mac} onChange={v => setQ({ ...q, mac: v })} placeholder="0e-2f-c4-…" />
          <Input label="From" type="datetime-local" value={q.from} onChange={v => setQ({ ...q, from: v })} />
          <Input label="To"   type="datetime-local" value={q.to}   onChange={v => setQ({ ...q, to: v })} />
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: 5 }}>Lodge</label>
            <FilterSelect
              value={q.lodge_id}
              onChange={v => setQ({ ...q, lodge_id: v })}
              options={[{ value: '', label: 'All lodges' }, ...lodges.map(l => ({ value: l.id, label: l.name }))]}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn type="submit" loading={loading}>Search</Btn>
            <Btn type="button" variant="ghost" onClick={reset}>Reset</Btn>
          </div>
        </form>
      </Card>

      <Card style={{ padding: 0, marginTop: 16 }}>
        <Table columns={columns} rows={rows} loading={loading} emptyText="No sessions match those filters." />
      </Card>

      <Pagination page={page} total={total} limit={25} onChange={(p) => { setPage(p); load(p); }} />
    </div>
  );
}
